<template>
    <Card :pageTitle="pageTitle">
        <div>
            <div class="mb-3">
                <Button noMargin @click="navigateToParent">
                    <i class="fas fa-chevron-left"></i> Back To Details
                </Button>
            </div>
            <div>
                <div class="drawer-title">
                    {{ isSafe ? "Safe" : "Drawer" }} <strong>#{{ safeDrawer.name }}</strong>
                </div>
                <div class="row">
                    <div class="col-md-6">
                        <CashDenominationForm
                            v-if="safeDrawer"
                            :showExpectedTotal="cashOutMode && !isSafe"
                            :expectedTotal="safeDrawer.totalCashAmount || 0"
                            @change="handleCashChange"
                        />
                        <CustomInput label="Notes" type="textarea" v-model="notes" />
                    </div>
                    <div class="col-md-6">
                        <CashPaymentModeValueForm
                            v-if="cashPaymentMode && cashOutMode && safeDrawerAmounts"
                            :safeDrawer="safeDrawer"
                            :safeDrawerAmounts="safeDrawerAmounts"
                            :showExpectedTotal="cashOutMode && !isSafe"
                            :paymentModes="
                                paymentModes.filter((item) => item.id != cashPaymentMode.id)
                            "
                            @change="handleNonCashChange"
                        />
                    </div>
                </div>

                <InfoPricedValue
                    noColor
                    v-if="safeDrawer"
                    :title="`Total Amount`"
                    :value="`${grandTotal}`"
                />
                <InfoPricedValue
                    noColor
                    v-if="safeDrawer"
                    :title="`Expected Total`"
                    :value="`${isSafe && !cashInMode ? safeOnHandCash : (isDrawer ? safeDrawer.totalCashAmount : safeDrawer.totalAmount)}`"
                />
                <InfoPricedValue
                    v-if="safeDrawer"
                    :title="`Difference`"
                    :value="`${round2(grandTotal - (isSafe && !cashInMode ? safeOnHandCash : (isDrawer ? safeDrawer.totalCashAmount : safeDrawer.totalAmount)))}`"
                />
                <div
                    v-if="safeDrawer && isDrawer && cashOutMode"
                    class="retained-float-summary"
                >
                    <p class="summary-title">Cash Out Summary</p>
                    <InfoPricedValue
                        noColor
                        :title="`Expected Cash In Drawer`"
                        :value="`${expectedCashInDrawer}`"
                    />
                    <InfoPricedValue
                        noColor
                        :title="`Retained Float`"
                        :value="`${expectedRetainedCashAmount}`"
                    />
                    <InfoPricedValue
                        noColor
                        :title="`Expected Cash Out`"
                        :value="`${expectedCashOutAmount}`"
                    />
                    <InfoPricedValue
                        noColor
                        :title="`Counted Cash`"
                        :value="`${countedCashAmount}`"
                    />
                    <InfoPricedValue
                        noColor
                        :title="`Actual Cash Out`"
                        :value="`${actualCashOutAmount}`"
                    />
                    <InfoPricedValue
                        :title="`Cash-Out Delta`"
                        :value="`${cashOutDelta}`"
                    />
                </div>

                <Button @click="navigateToParent" class="me-2" borderOnly>Cancel</Button>
                <Button @click="cashInOutSafeDrawer">{{
                    processing ? "Please wait..." : modeName
                }}</Button>
            </div>
        </div>
    </Card>
</template>

<script>
import { http } from "@/utils/http"
import Paginator from "../../components/other/Paginator.vue"
import Card from "../../components/UI/Card.vue"
import CashManagementFetchApi from "@/utils/fetch/cash-management-fetch-api"
import CustomTable from "../../components/layout/CustomTable.vue"
import DetailContainer from "../../components/layout/DetailContainer.vue"
import InfoListItem from "../../components/UI/InfoListItem.vue"
import Button from "../../components/UI/Button.vue"
import CashDrawerCard from "../../components/cash-management/CashDrawerCard.vue"
import CreateDrawerBtn from "../../components/cash-management/CreateDrawerBtn.vue"
import CustomInput from "../../components/crud/custom-input.vue"
import CashDenominationForm from "../../components/cash-management/CashDenominationForm.vue"
import CashPaymentModeValueForm from "../../components/cash-management/CashPaymentModeValueForm.vue"
import InfoPricedValue from "../../components/UI/InfoPricedValue.vue"

import { errMsg, round2 } from "../../utils/common"

export default {
    data() {
        return {
            safeDrawer: null,
            notes: "",
            paymentModes: [],
            loading: false,
            pageTitle: "",
            modeName: "",
            actionTitle: "",
            isSafe: false,
            isDrawer: false,
            safeDrawerTitle: "",
            processing: false,
            cashDenominations: {},
            cashTotalAmount: 0,
            cashPaymentMode: null,
            cashInMode: false,
            safeDrawerAmounts: null,
            nonCashPaymentValues: null,
            nonCashTotalAmount: 0,
            safeOnHandCash: 0,  // For safes: actual cash on hand
            expectedCashInDrawer: 0,
            expectedCashOutAmount: 0,
            expectedRetainedCashAmount: 0,
        }
    },
    computed: {
        cashOutMode() {
            return !this.cashInMode
        },
        grandTotal() {
            return (
                round2(Number(this.cashTotalAmount || 0)) +
                round2(Number(this.nonCashTotalAmount || 0))
            )
        },
        countedCashAmount() {
            return round2(Number(this.cashTotalAmount || 0))
        },
        actualCashOutAmount() {
            if (!(this.isDrawer && this.cashOutMode)) return this.countedCashAmount
            return round2(Math.max(this.countedCashAmount - Number(this.expectedRetainedCashAmount || 0), 0))
        },
        cashOutDelta() {
            if (!(this.isDrawer && this.cashOutMode)) return 0
            return round2(this.actualCashOutAmount - Number(this.expectedCashOutAmount || 0))
        },
    },
    components: {
        CashDrawerCard,
        Button,
        DetailContainer,
        Card,
        Paginator,
        CustomTable,
        InfoListItem,
        CreateDrawerBtn,
        CustomInput,
        CashDenominationForm,
        CashPaymentModeValueForm,
        InfoPricedValue,
    },
    async mounted() {
        this.fetchData()
    },
    methods: {
        round2,
        async fetchData() {
            this.loading = true
            try {
                this.safeDrawer = await CashManagementFetchApi.fetchSafeOrDrawerDetail(
                    this.$route.params.id
                )
                this.isSafe = !this.safeDrawer.parent
                this.isDrawer = this.safeDrawer.parent
                this.safeDrawerTitle = this.isSafe ? "Safe" : "Drawer"

                this.cashInMode = this.$route.params.mode == "open" ? true : false
                this.modeName =
                    this.$route.params.mode == "open"
                        ? this.isSafe
                            ? "Open"
                            : "Cash In"
                        : this.isSafe
                        ? "Close"
                        : "Cash Out"

                this.actionTitle = `${this.modeName} ${this.safeDrawerTitle}`
                this.pageTitle = this.actionTitle
                let res = await http.get("/mode-of-payments")
                this.paymentModes = res.data.result
                this.cashPaymentMode = this.paymentModes.find(
                    (item) => item.name.toLowerCase() == "cash"
                )

                // fetch the cash drawer amounts
                res = await http.get(`/safe-drawer-amounts?safe_drawer_id=${this.safeDrawer.id}`)
                this.safeDrawerAmounts = res.data.result

                // For SAFES in CLOSE mode: fetch actual safe on-hand cash (not cached totalAmount)
                if (this.isSafe && !this.cashInMode) {
                    const summaryRes = await CashManagementFetchApi.fetchSafeOrDrawerSummary(this.$route.params.id)
                    this.safeOnHandCash = summaryRes.result.safeOnHandCash || 0
                }

                if (this.isDrawer && this.cashOutMode) {
                    try {
                        const expectedPayload = await CashManagementFetchApi.fetchExpectedAmountsPayload(
                            this.$route.params.id
                        )
                        this.expectedCashInDrawer = round2(Number(expectedPayload.expectedCashInDrawer || 0))
                        this.expectedCashOutAmount = round2(Number(expectedPayload.expectedCashOutAmount || 0))
                        this.expectedRetainedCashAmount = round2(
                            Number(expectedPayload.expectedRetainedCashAmount || 0)
                        )
                    } catch (error) {
                        this.expectedCashInDrawer = round2(Number(this.safeDrawer.totalCashAmount || 0))
                        this.expectedRetainedCashAmount = 0
                        this.expectedCashOutAmount = this.expectedCashInDrawer
                    }
                }
            } catch (err) {
                console.log("error in fetching safe drawer detail", err)
            }
            this.loading = false
        },

        async handleCashChange(data) {
            this.cashDenominations = data.denominations
            this.cashTotalAmount = data.totalAmount
        },
        async handleNonCashChange(data) {
            this.nonCashPaymentValues = data.actualValues
            this.nonCashTotalAmount = data.totalAmount
        },
        navigateToParent() {
            this.$router.push(`/safe-drawers/${this.$route.params.id}`)
        },
        getCashOutErrorMessage(err) {
            const errorCode = err?.response?.data?.error?.code || ""
            const errorPayload = err?.response?.data?.error || {}

            if (errorCode === "DRAWER_RETAINED_FLOAT_SHORTAGE") {
                const counted = Number(errorPayload.countedCashAmount || 0).toFixed(2)
                const retained = Number(errorPayload.retainedFloatTarget || 0).toFixed(2)
                return `Counted cash ($${counted}) is below retained float target ($${retained}).`
            }
            if (errorCode === "DRAWER_RETAINED_FLOAT_DENOMINATION_MISMATCH") {
                return "Cash denomination counts do not match entered cash value. Please correct counts."
            }
            return errMsg(err, "Could not perform transaction!")
        },
        async cashInOutSafeDrawer() {
            if (this.processing) return
            // cash in or open same
            // only cash allowed in cash in

            const cashAmountData = {
                modeOfPaymentId: this.cashPaymentMode.id,
                value: this.cashTotalAmount,
            }

            for (const key in this.cashDenominations) {
                cashAmountData[key] = Number(this.cashDenominations[key].amount || 0)
            }

            const nonCashData = Object.keys(this.nonCashPaymentValues || {}).map(
                (modeOfPaymentId) => ({
                    modeOfPaymentId: modeOfPaymentId,
                    value: round2(this.nonCashPaymentValues[modeOfPaymentId]),
                })
            )

            const cashDrawerAmountData = {
                safeDrawerId: this.safeDrawer.id,
                data: [cashAmountData, ...nonCashData],
                notes: this.notes, // COVA-STYLE: Include notes for audit trail
            }

            // console.log("cash drawer amount data", cashDrawerAmountData, this.nonCashPaymentValues)
            this.processing = true
            try {
                await CashManagementFetchApi[
                    this.cashInMode ? "safeDrawerCashIn" : "safeDrawerCashOut"
                ](cashDrawerAmountData)
                this.navigateToParent()
                this.$toast.success(`${this.actionTitle} success`)
            } catch (err) {
                console.log("error in cashing in", err)
                this.$toast.error(
                    this.cashOutMode ? this.getCashOutErrorMessage(err) : errMsg(err, "Could not perform transaction!")
                )
            }
            this.processing = false
        },
    },
}
</script>

<style lang="scss" scoped>
@import "../../assets/variables.scss";
.drawer-title {
    font-size: 1.3em;
}
.retained-float-summary {
    margin: 14px 0;
    padding: 12px;
    border: 1px solid #dfe3e8;
    border-radius: 8px;
    background: #f7f9fb;
}
.summary-title {
    font-weight: 600;
    margin: 0 0 8px 0;
}
/* Tokenized spacing for action bar */
.mb-3 { margin-bottom: $sectionGap !important; }
</style>
